import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { collection, query, where, onSnapshot, getDoc, doc, Timestamp } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import Footer from '../components/Footer';
import { getInitiatorPedalIds } from '../utils/tradeUtils';
import { resolvePedalDisplayFromTrade } from '../utils/tradeOfferPedalSnapshots';
import { isResendEligible, buildResendPrefill, resendPedalIds } from '../utils/expiredOfferResend';
import { useReturnViewState, useRestoreScroll, OFFER_FLOW_PREFIXES, OFFER_HISTORY_VIEW_KEY } from '../hooks/useReturnViewState';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import { placeholderFor } from '../utils/listingPlaceholder';

// Tab + scroll memory for coming back from the other offer pages (see
// hooks/useReturnViewState.js for the rules).

const OfferHistory = () => {
  const [sentOffers, setSentOffers] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usernames, setUsernames] = useState({});
  const [pedalData, setPedalData] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  // Per-card state for "Resend offer" on expired received trades:
  // { [offerId]: { checking: bool, message: string|null } }
  const [resendState, setResendState] = useState({});
  // Received / Sent tabs, same system as the Offers page (TradeCenter):
  // one list at a time at full width instead of two squeezed columns.
  const { savedTab, scrollSnapshot, saveView } = useReturnViewState({
    storageKey: OFFER_HISTORY_VIEW_KEY,
    flowPrefixes: OFFER_FLOW_PREFIXES,
  });
  const [activeTab, setActiveTab] = useState(() => savedTab || 'received');
  // `loading` clears on the FIRST listener; the restore waits for all four
  // lists (trade + purchase, sent + received) to have delivered once.
  const listsFiredRef = useRef(new Set());
  const [listsReady, setListsReady] = useState(false);
  // Pedal-read batches still in flight. The Resend button appears only once
  // its pedals are checked and, on phones, adds a whole row to a band — so a
  // restore waits for these reads too, or those rows would push the user down
  // after they'd been put back.
  const [pedalReadsPending, setPedalReadsPending] = useState(0);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const markListFired = (name) => {
    listsFiredRef.current.add(name);
    if (listsFiredRef.current.size === 4) setListsReady(true);
  };
  // Current listing docs by id, captured from the reads fetchPedalData
  // already makes for thumbnails (no extra reads). null = confirmed deleted;
  // absent = not known yet (or unreadable offline). Drives whether the
  // Resend button shows at all.
  const [liveListings, setLiveListings] = useState({});
  // Ids already requested (in flight or done). fetchUsernames/fetchPedalData
  // are called from the four snapshot listeners, whose closures see the
  // FIRST render's `usernames`/`pedalData` ({}), so a state-based "already
  // fetched?" check never skipped anything and every listener fire re-read
  // every profile and listing. Refs are current in every closure.
  const requestedUidsRef = useRef(new Set());
  const requestedPedalIdsRef = useRef(new Set());
  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        setIsHeaderCompact(y > 400);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, currentUser]);

  const headerLinkBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    border: '1px solid #C89F3C',
    backgroundColor: 'white',
    color: '#374151',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '0.875rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const renderMobileChromeBar = () => (
    <div className="oh-compact-bar-inner">
      <span className="oh-compact-title">Offer History</span>
      <span className="oh-compact-spacer" aria-hidden="true" />
      <Link to="/trade-center" className="oh-link-btn" style={headerLinkBtnStyle}>
        Active Offers
      </Link>
    </div>
  );

  const fetchUsernames = async (offersList) => {
    const fromUserIds = offersList.map(o => o.fromUserId || o.initiatorId).filter(Boolean);
    const toUserIds = offersList.map(o => o.toUserId || o.responderId).filter(Boolean);
    const toFetch = [...new Set([...fromUserIds, ...toUserIds])]
      .filter((uid) => !requestedUidsRef.current.has(uid));
    if (toFetch.length === 0) return;
    toFetch.forEach((uid) => requestedUidsRef.current.add(uid));

    // In parallel (these used to load one at a time).
    const entries = await Promise.all(toFetch.map(async (uid) => {
      try {
        // PII isolation: read sanitized public mirror.
        const userDoc = await getDoc(doc(db, 'publicProfiles', uid));
        const data = userDoc.exists() ? userDoc.data() : {};
        return [uid, { username: data.username || 'Unknown', photoURL: data.photoURL || null }];
      } catch (err) {
        requestedUidsRef.current.delete(uid); // let a later update retry
        return [uid, { username: 'Unknown', photoURL: null }];
      }
    }));

    setUsernames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
  };

  const pedalImageFromRaw = (raw) => {
    if (!raw) return null;
    // Photo-less listing: its stand-in (carried on snapshots as placeholderImage).
    return raw.images?.top || raw.images?.upper || raw.imageUrl || placeholderFor(raw).url;
  };

  const displayFromResolvedPedal = (resolved) => {
    if (!resolved) return null;
    return {
      make: resolved.make || '',
      model: resolved.model || '',
      image: pedalImageFromRaw(resolved),
      condition: resolved.condition || '',
      price: resolved.price || 0,
    };
  };

  const getPedalDisplay = (pedalId, offer) => {
    if (!pedalId) return null;
    const live = pedalData[pedalId];
    const livePedal = live
      ? {
          id: pedalId,
          make: live.make,
          model: live.model,
          condition: live.condition,
          price: live.price,
          images: { top: live.image },
          imageUrl: live.image,
        }
      : null;
    const resolved = resolvePedalDisplayFromTrade(offer, pedalId, livePedal);
    if (resolved) return displayFromResolvedPedal(resolved);
    return live || null;
  };

  const fetchPedalData = async (offersList) => {
    const pedalIdToOffer = new Map();

    for (const offer of offersList) {
      if (offer.offerType === 'trade') {
        const allInitiatorIds = getInitiatorPedalIds(offer);
        allInitiatorIds.forEach((id) => {
          if (!pedalIdToOffer.has(id)) pedalIdToOffer.set(id, offer);
        });
        if (offer.responderPedalId && !pedalIdToOffer.has(offer.responderPedalId)) {
          pedalIdToOffer.set(offer.responderPedalId, offer);
        }
      } else if (offer.targetPedalId && !pedalIdToOffer.has(offer.targetPedalId)) {
        pedalIdToOffer.set(offer.targetPedalId, offer);
      }
    }

    const toFetch = [...pedalIdToOffer.keys()].filter((id) => !requestedPedalIdsRef.current.has(id));
    if (toFetch.length === 0) return;
    toFetch.forEach((id) => requestedPedalIdsRef.current.add(id));

    // In parallel (these used to load one at a time, and nothing rendered
    // until the last one finished).
    setPedalReadsPending((n) => n + 1);
    const results = await Promise.all(toFetch.map(async (pedalId) => {
      const offer = pedalIdToOffer.get(pedalId);
      let livePedal = null;
      let live; // undefined = unknown (offline cache-miss / read error)
      try {
        const pedalDoc = await getDoc(doc(db, 'listings', pedalId));
        if (pedalDoc.exists()) {
          livePedal = { id: pedalId, ...pedalDoc.data() };
          live = livePedal;
        } else if (!pedalDoc.metadata?.fromCache) {
          // Server-confirmed missing. An offline cache-miss stays unknown.
          live = null;
        }
      } catch (err) {
        // Listing may have been deleted, or the read failed.
      }
      // Unknown (offline cache-miss or failed read): let a later listener
      // update retry it, rather than leaving a loading tile forever.
      if (live === undefined) requestedPedalIdsRef.current.delete(pedalId);

      let display = null;
      if (offer?.offerType === 'trade') {
        display = displayFromResolvedPedal(resolvePedalDisplayFromTrade(offer, pedalId, livePedal));
      } else if (offer?.targetPedalId === pedalId && offer.pedalSnapshot?.targetPedal) {
        display = displayFromResolvedPedal({ id: pedalId, ...offer.pedalSnapshot.targetPedal });
      } else if (livePedal) {
        display = displayFromResolvedPedal(livePedal);
      }
      return { pedalId, display, live };
    }));

    const newPedalData = {};
    const newLive = {};
    for (const { pedalId, display, live } of results) {
      if (display) newPedalData[pedalId] = display;
      if (live !== undefined) newLive[pedalId] = live;
    }
    if (Object.keys(newPedalData).length > 0) {
      setPedalData((prev) => ({ ...prev, ...newPedalData }));
    }
    if (Object.keys(newLive).length > 0) {
      setLiveListings((prev) => ({ ...prev, ...newLive }));
    }
    setPedalReadsPending((n) => n - 1);
  };

  // Would "Resend offer" work right now, judged from the listings already
  // loaded for the thumbnails? The button shows only when this is ok; the
  // click re-checks against fresh reads, since the page can sit open.
  const resendReady = (offer) => {
    const myUid = currentUser?.uid || auth.currentUser?.uid;
    if (!isResendEligible(offer, myUid)) return false;
    const ids = resendPedalIds(offer);
    if (ids.some((id) => !(id in liveListings))) return false; // not known yet
    const listingsById = Object.fromEntries(ids.map((id) => [id, liveListings[id]]));
    return buildResendPrefill({ offer, myUid, listingsById }).ok;
  };

  const getStatusLabel = (offer) => {
    if (offer.status === 'expired') return 'Expired';
    if (offer.status === 'cancelled') return 'Cancelled';
    if (offer.declinedReason) return 'Auto-Declined';
    return 'Declined';
  };

  useEffect(() => {
    // Offer listeners live OUTSIDE the auth callback: Firebase ignores a
    // cleanup returned from onAuthStateChanged, so the old inner `return` never
    // ran and every visit to this page left four live listeners behind for
    // the rest of the session (found in the 2026-09-29 review).
    let offerUnsubs = [];
    const stopOfferListeners = () => {
      offerUnsubs.forEach((unsub) => unsub());
      offerUnsubs = [];
    };

    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      stopOfferListeners(); // an auth change (sign-out / switch) drops the old user's
      setCurrentUser(user);
      if (!user) {
        setLoading(false);
        return;
      }

      const ninetyDaysAgo = Timestamp.fromDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));

      const tradeSentQuery = query(
        collection(db, 'tradeOffers'),
        where('fromUserId', '==', user.uid),
        where('status', 'in', ['declined', 'expired', 'cancelled'])
      );

      const tradeReceivedQuery = query(
        collection(db, 'tradeOffers'),
        where('toUserId', '==', user.uid),
        where('status', 'in', ['declined', 'expired', 'cancelled'])
      );

      const purchaseSentQuery = query(
        collection(db, 'purchaseOffers'),
        where('initiatorId', '==', user.uid),
        where('status', 'in', ['declined', 'expired', 'cancelled'])
      );

      const purchaseReceivedQuery = query(
        collection(db, 'purchaseOffers'),
        where('responderId', '==', user.uid),
        where('status', 'in', ['declined', 'expired', 'cancelled'])
      );

      const filterByDate = (offers) => {
        return offers.filter(o => {
          if (!o.createdAt) return false;
          const createdDate = o.createdAt.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
          return createdDate >= ninetyDaysAgo.toDate();
        });
      };

      const sortByDate = (offers) => {
        return offers.sort((a, b) => {
          const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return bDate - aDate;
        });
      };

      let tradeSent = [];
      let purchaseSent = [];
      let tradeReceived = [];
      let purchaseReceived = [];

      const updateSent = () => {
        const combined = filterByDate([...tradeSent, ...purchaseSent]);
        const sorted = sortByDate(combined);
        setSentOffers(sorted);
        fetchUsernames(sorted);
        fetchPedalData(sorted);
      };

      const updateReceived = () => {
        const combined = filterByDate([...tradeReceived, ...purchaseReceived]);
        const sorted = sortByDate(combined);
        setReceivedOffers(sorted);
        fetchUsernames(sorted);
        fetchPedalData(sorted);
      };

      const unsub1 = onSnapshot(tradeSentQuery, (snapshot) => {
        tradeSent = snapshot.docs.map(d => ({ id: d.id, ...d.data(), offerType: 'trade' }));
        updateSent();
        markListFired('sentTrade');
        setLoading(false);
      }, (err) => {
        // A failed list still counts as "arrived", so neither the loading
        // state nor the restore can be stranded waiting on it.
        console.error('Offer History listener failed (sentTrade):', err);
        markListFired('sentTrade');
        setLoading(false);
      });

      const unsub2 = onSnapshot(purchaseSentQuery, (snapshot) => {
        purchaseSent = snapshot.docs.map(d => ({ id: d.id, ...d.data(), offerType: 'purchase' }));
        updateSent();
        markListFired('sentPurchase');
      }, (err) => {
        // A failed list still counts as "arrived", so neither the loading
        // state nor the restore can be stranded waiting on it.
        console.error('Offer History listener failed (sentPurchase):', err);
        markListFired('sentPurchase');
      });

      const unsub3 = onSnapshot(tradeReceivedQuery, (snapshot) => {
        tradeReceived = snapshot.docs.map(d => ({ id: d.id, ...d.data(), offerType: 'trade' }));
        updateReceived();
        markListFired('receivedTrade');
      }, (err) => {
        // A failed list still counts as "arrived", so neither the loading
        // state nor the restore can be stranded waiting on it.
        console.error('Offer History listener failed (receivedTrade):', err);
        markListFired('receivedTrade');
      });

      const unsub4 = onSnapshot(purchaseReceivedQuery, (snapshot) => {
        purchaseReceived = snapshot.docs.map(d => ({ id: d.id, ...d.data(), offerType: 'purchase' }));
        updateReceived();
        markListFired('receivedPurchase');
      }, (err) => {
        // A failed list still counts as "arrived", so neither the loading
        // state nor the restore can be stranded waiting on it.
        console.error('Offer History listener failed (receivedPurchase):', err);
        markListFired('receivedPurchase');
      });

      offerUnsubs = [unsub1, unsub2, unsub3, unsub4];
    });

    return () => {
      unsubscribeAuth();
      stopOfferListeners();
    };
  }, []);


  const saveViewState = () => saveView(activeTab);

  // Same safety net as Offers: always start at the top (the SPA carries the
  // previous page's scroll offset into this one), and let the restore cancel
  // it the moment it actually starts. If the restore never runs, the user is
  // at the top rather than stranded mid-page.
  const cancelScrollTopRef = useRef(null);
  useLayoutEffect(() => {
    const cancel = scrollPageToTop({ blur: false });
    cancelScrollTopRef.current = cancel;
    return cancel;
  }, []);

  useRestoreScroll(scrollSnapshot, listsReady && pedalReadsPending === 0, {
    onStart: () => cancelScrollTopRef.current?.(),
  });

  // "Resend offer" for an expired trade offer the user received. Checks the
  // pedals' CURRENT state at the moment of the click (no extra reads just to
  // view history), then opens the normal offer page pre-filled with the
  // mirrored terms. The offer page re-checks availability again at send.
  const handleResend = async (offer) => {
    const myUid = currentUser?.uid || auth.currentUser?.uid;
    if (!isResendEligible(offer, myUid)) return;
    if (resendState[offer.id]?.checking) return;
    // Save the spot at the TAP (the tab and scroll the user is actually on),
    // not after the awaited checks below, by which time they may have
    // switched tabs or left the page.
    saveViewState();
    setResendState((prev) => ({ ...prev, [offer.id]: { checking: true, message: null } }));
    try {
      const ids = resendPedalIds(offer);
      const snaps = await Promise.all(ids.map((id) => getDoc(doc(db, 'listings', id))));
      const listingsById = {};
      for (let i = 0; i < ids.length; i++) {
        const snap = snaps[i];
        // An offline cache-miss is "can't see it", not "deleted".
        if (!snap.exists() && snap.metadata?.fromCache) {
          throw Object.assign(new Error('offline'), { code: 'unavailable' });
        }
        listingsById[ids[i]] = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      }
      // Fold the fresh reads into what the page knows, so the button's
      // visibility (resendReady) reflects the pedals' state right now.
      setLiveListings((prev) => ({ ...prev, ...listingsById }));
      const result = buildResendPrefill({ offer, myUid, listingsById });
      if (!result.ok) {
        // Something changed while the page sat open (a pedal traded, hidden,
        // or deactivated). No "can't resend" message by design: the fresh
        // data above makes resendReady false, so the button just goes away.
        setResendState((prev) => ({ ...prev, [offer.id]: { checking: false, message: null } }));
        return;
      }
      setResendState((prev) => ({ ...prev, [offer.id]: { checking: false, message: null } }));
      // They left Offer History while the pedals were being checked: don't
      // yank them to the offer screen from wherever they went.
      if (!mountedRef.current || window.location.pathname !== '/offer-history') return;
      navigate(`/direct-offer?targetPedalId=${encodeURIComponent(result.targetPedalId)}`, {
        state: {
          resendPrefill: {
            fromOfferId: offer.id,
            targetPedalId: result.targetPedalId,
            myPedalIds: result.myPedalIds,
            offerType: result.offerType,
            cashAmount: result.cashAmount,
            droppedMyPedalNames: result.droppedMyPedalNames,
          },
        },
      });
    } catch (err) {
      console.error('Resend availability check failed:', err);
      setResendState((prev) => ({
        ...prev,
        [offer.id]: { checking: false, message: "Couldn't check the pedals right now. Check your connection and try again." },
      }));
    }
  };

  const renderTile = (pedalId, offer, stacked = false) => {
    if (!pedalId) return null; // e.g. a legacy trade with no responder pedal
    const pedal = getPedalDisplay(pedalId, offer);
    const frame = stacked
      ? { border: '3px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }
      : {};
    // Same-size placeholder while loading, so late data never shifts the
    // layout (see TradeCenter's renderTilePlaceholder).
    if (!pedal) {
      // Server-confirmed deleted with no stored snapshot: a labeled empty
      // tile, not a placeholder that looks like it's still loading.
      if (pedalId in liveListings && liveListings[pedalId] === null) {
        return (
          <div className="tc-tile" style={{ backgroundColor: '#f3f4f6', borderRadius: '12px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', ...frame }}>
            <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No img</span>
          </div>
        );
      }
      return <div className="tc-tile" aria-hidden="true" style={{ backgroundColor: '#f3f4f6', borderRadius: '12px', ...frame }} />;
    }
    return pedal.image ? (
      <img
        src={pedal.image}
        alt={`${pedal.make} ${pedal.model}`}
        className="tc-tile"
        style={{ objectFit: 'cover', borderRadius: '12px', ...frame }}
      />
    ) : (
      <div className="tc-tile" style={{
        backgroundColor: '#f3f4f6', borderRadius: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid #e5e7eb', ...frame
      }}>
        <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No img</span>
      </div>
    );
  };

  // Same band layout as the Offers page (TradeCenter renderOfferBand), for
  // past offers: the status is Declined / Expired / Cancelled, there is no
  // expiry clock, and the footer carries the decline reason when there is one.
  const renderOfferBand = (offer, direction) => {
    const isSent = direction === 'sent';
    const otherUserId = offer.offerType === 'purchase'
      ? (isSent ? offer.responderId : offer.initiatorId)
      : (isSent ? offer.toUserId : offer.fromUserId);

    const offerLink = offer.offerType === 'purchase'
      ? `/purchase-offer-details/${offer.id}?from=history`
      : `/offer/${offer.id}?from=history`;

    const userData = usernames[otherUserId];
    const username = typeof userData === 'object' ? userData?.username : (userData || 'Unknown');
    const photoURL = typeof userData === 'object' ? userData?.photoURL : null;
    const initials = (username || '??').slice(0, 2).toUpperCase();

    const dateStr = offer.createdAt?.toDate
      ? `${offer.createdAt.toDate().toLocaleDateString()} · ${offer.createdAt.toDate().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : 'N/A';

    const cashAmount = offer.offerType === 'purchase'
      ? (offer.counterAmount ?? offer.offerAmount ?? 0)
      : (offer.counterAmount ?? offer.cashAmount ?? 0);
    const hasCash = Number(cashAmount) > 0;

    const statusLabel = getStatusLabel(offer);
    const statusColor = offer.status === 'expired' ? '#92400e' : offer.status === 'cancelled' ? '#374151' : '#991b1b';

    // Who pays: cashFlow when present (rewritten on every counter), legacy
    // `type` otherwise; purchases always run buyer (initiator) -> seller.
    const myUid = currentUser?.uid || auth.currentUser?.uid;
    let cashPayerIsMe;
    if (offer.offerType === 'purchase') {
      cashPayerIsMe = offer.initiatorId === myUid;
    } else if (offer.cashFlow?.payer) {
      cashPayerIsMe = offer.cashFlow.payer === myUid;
    } else {
      const legacyPayer = offer.type === 'ask' ? offer.toUserId : offer.fromUserId;
      cashPayerIsMe = legacyPayer === myUid;
    }
    // Who proposed the terms on the table: the last offer/counter, NOT the
    // last response — on a declined offer the last response is the decline,
    // which says nothing about whose terms these were.
    const proposals = offer.history?.filter(
      (e) => e.type === 'offer' || (e.type === 'response' && e.responseType === 'counter')
    ) || [];
    const lastProposer = proposals.length > 0 ? proposals[proposals.length - 1].from : null;
    const proposerIsMe = lastProposer ? lastProposer === myUid : isSent;
    const cashPhrase = (proposerIsMe ? 'You ' : 'They ')
      + (proposerIsMe === cashPayerIsMe ? 'offered' : 'asked for');
    const dealLabel = offer.offerType === 'purchase'
      ? 'Purchase'
      : (hasCash ? 'Trade + cash' : 'Straight trade');

    const initiatorIds = offer.offerType === 'trade' ? getInitiatorPedalIds(offer) : [];
    const isMulti = initiatorIds.length > 1;
    const initiatorLabel = `${isSent ? 'Your' : 'Their'} pedal${isMulti ? `s (${initiatorIds.length})` : ''}`;

    const showResend = direction === 'received' && resendReady(offer);

    return (
      <div key={offer.id} className="tc-band" data-restore-anchor={offer.id}>
        <div className="tc-band-head">
          <div className="tc-band-avatar">
            {photoURL
              ? <img src={photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials}
          </div>
          <div className="tc-band-who">
            <div className="tc-band-name">
              <span className="tc-band-from">{isSent ? 'Sent to' : 'Offer from'}</span>
              {username}
            </div>
            <div className="tc-band-date">{dateStr}</div>
          </div>
          <span className="tc-band-status" style={{ color: statusColor }}>{statusLabel}</span>
        </div>

        <div className="tc-band-body">
          {/* data-swipe-back-opt-out: this horizontal strip owns its own
              scrolling; gestures born here must never read as the global
              swipe-back. Replaces the route-level opt-out that killed
              back-swipe on the whole page (removed 2026-10-02; see
              startedInOptOut in hooks/useSwipeViews.js). */}
          <div className="tc-band-pedals" data-swipe-back-opt-out="true">
            {offer.offerType === 'trade' ? (
              <>
                <div className="tc-band-side">
                  {isMulti ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {initiatorIds.map((id, idx) => (
                        <div key={id} className={idx === 0 ? '' : 'tc-tile-stacked'} style={{ zIndex: initiatorIds.length - idx, position: 'relative' }}>
                          {renderTile(id, offer, true)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    renderTile(initiatorIds[0], offer)
                  )}
                  <div className="tc-band-side-label">{initiatorLabel}</div>
                </div>
                <span className="tc-band-arrow" aria-hidden="true">⇄</span>
                <div className="tc-band-side">
                  {renderTile(offer.responderPedalId, offer)}
                  <div className="tc-band-side-label">{isSent ? 'Their pedal' : 'Your pedal'}</div>
                </div>
              </>
            ) : (
              <div className="tc-band-side">
                {renderTile(offer.targetPedalId, offer)}
                <div className="tc-band-side-label">{isSent ? 'Their pedal' : 'Your pedal'}</div>
              </div>
            )}
          </div>

          <div className="tc-band-figures">
            <div className="tc-band-figure">
              <span className="tc-band-figure-label">Offer type</span>
              <span className="tc-band-figure-value">{dealLabel}</span>
            </div>
            {hasCash && (
              <div className="tc-band-figure">
                <span className="tc-band-figure-label">Cash</span>
                <span className="tc-band-figure-value tc-band-amount">
                  <span className="tc-band-amount-who tc-wide-only">{cashPhrase}</span>
                  <span>${cashAmount}</span>
                  <span className="tc-band-amount-who tc-narrow-only">{cashPayerIsMe ? 'from you' : 'to you'}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Resend: trade offers you RECEIVED that expired, or that you
            declined and changed your mind about. Shown ONLY when it will work
            (resendReady): never on sent offers, offers the other person
            declined, auto-declined offers, multi-pedal offers, or offers whose
            pedals are unavailable. The only message is the offline
            "couldn't check". */}
        <div className="tc-band-actions">
          <Link to={offerLink} className="tc-band-cta" onClick={saveViewState}>View offer details</Link>
          {showResend && (
            <button
              type="button"
              onClick={() => handleResend(offer)}
              disabled={!!resendState[offer.id]?.checking}
              className="tc-band-secondary oh-band-resend"
            >
              {resendState[offer.id]?.checking ? 'Checking pedals…' : 'Resend offer'}
            </button>
          )}
        </div>
        {showResend && resendState[offer.id]?.message && (
          <p className="oh-band-resend-msg">{resendState[offer.id].message}</p>
        )}

        {offer.declinedReason && (
          <div className="tc-band-foot">
            <span className="oh-band-reason">{offer.declinedReason}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div
        className="offer-history-page"
        style={{
          backgroundColor: '#F7F3EB',
          flex: 1,
          flexDirection: 'column',
          padding: '0 1rem',
          display: 'flex',
          minWidth: 0,
          overflowX: 'clip',
        }}
      >
        <div className="offer-history-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem', flex: 1, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
          <div className={`oh-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
            <style>{`
              .oh-compact-bar-inner {
                max-width: 1100px;
                margin: 0 auto;
                width: 100%;
                box-sizing: border-box;
                display: flex;
                justify-content: flex-start;
                align-items: center;
                gap: 12px;
              }
              .oh-compact-title {
                font-family: Nunito, sans-serif;
                font-weight: 700;
                font-size: 1.125rem;
                color: #111827;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                min-width: 0;
                flex: 0 0 auto;
                text-align: left;
              }
              .oh-compact-spacer {
                flex: 1 1 auto;
                min-width: 0;
              }
              .oh-sticky-header .oh-link-btn {
                flex-shrink: 0;
              }
              .oh-sticky-header .oh-header-expanded {
                display: block;
              }
              .oh-sticky-header .oh-mobile-chrome-slot {
                display: none;
              }
              .oh-sticky-header .oh-compact-bar-mobile-fixed {
                display: none;
              }
              @media (min-width: 769px) {
                .oh-sticky-header .oh-compact-bar-mobile-fixed {
                  display: none !important;
                }
                .oh-sticky-header .oh-compact-bar {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  z-index: 100;
                  padding: 0.6rem 1rem 0.7rem 1rem;
                  background-color: #F7F3EB;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                  box-sizing: border-box;
                  opacity: 0;
                  pointer-events: none;
                  transition: opacity 0.2s ease;
                }
                .oh-sticky-header.is-compact .oh-compact-bar {
                  opacity: 1;
                  pointer-events: auto;
                }
                .oh-sticky-header .oh-link-btn {
                  padding: 0.6rem 1.4rem !important;
                }
              }
              @media (max-width: 768px) {
                .offer-history-page-content {
                  padding-top: 0 !important;
                }
                .oh-sticky-header .oh-header-expanded {
                  display: none !important;
                }
                .oh-sticky-header .oh-mobile-chrome-slot {
                  display: block !important;
                  position: relative;
                  flex-shrink: 0;
                  background-color: #F7F3EB;
                  overflow: hidden;
                  margin-left: -0.75rem;
                  margin-right: -0.75rem;
                  width: calc(100% + 1.5rem);
                }
                .oh-sticky-header .oh-compact-bar-inflow {
                  display: block;
                  padding: 0.6rem 1rem 1.5rem 1rem;
                  background-color: #F7F3EB;
                  box-sizing: border-box;
                }
                .oh-sticky-header .oh-compact-bar-inflow.is-suppressed {
                  visibility: hidden;
                  pointer-events: none;
                }
                .oh-sticky-header .oh-compact-bar-desktop {
                  display: none !important;
                }
                .oh-sticky-header .oh-compact-bar-mobile-fixed {
                  display: block;
                }
                .oh-sticky-header .oh-compact-bar {
                  display: none;
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  z-index: 100;
                  padding: 0.6rem 1rem 0.85rem 1rem;
                  background-color: #F7F3EB;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                  box-sizing: border-box;
                  pointer-events: none;
                  transform: translateY(-100%);
                  transition: transform 0.2s ease;
                }
                .oh-sticky-header .oh-compact-bar.is-mobile-visible {
                  display: block;
                  transform: translateY(0);
                  pointer-events: auto;
                }
                .oh-sticky-header .oh-compact-title {
                  font-size: calc(1rem + 1pt) !important;
                  color: #5A9ECB !important;
                }
                .oh-sticky-header .oh-link-btn {
                  padding: 0.5rem 1rem !important;
                  font-size: 0.875rem !important;
                }
              }
            `}</style>

            <div
              ref={mobileChromeSlotRef}
              className="oh-mobile-chrome-slot"
              style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
            >
              <div
                ref={mobileChromeInflowRef}
                className={['oh-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
              >
                {renderMobileChromeBar()}
              </div>
            </div>

            <div
              className={[
                'oh-compact-bar',
                showMobileChromeBar && 'is-mobile-visible',
              ].filter(Boolean).join(' ')}
            >
              <div className="oh-compact-bar-mobile-fixed">
                {renderMobileChromeBar()}
              </div>
              <div className="oh-compact-bar-desktop">
                {renderMobileChromeBar()}
              </div>
            </div>

            <div className="oh-header-expanded">
              <Link
                to="/trade-center"
                className="offer-history-header-back-link"
                style={{
                  color: '#5A9ECB',
                  textDecoration: 'none',
                  alignItems: 'center',
                  marginBottom: '1rem',
                  fontSize: '1.125rem'
                }}
              >
                ← Back to Offers
              </Link>
              <div className="offer-history-header-expanded-inner">
                <div className="offer-history-header-copy">
                  <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                    Offer History
                  </h1>
                  <p className="offer-history-header-description text-gray-600" style={{ fontSize: '17px' }}>
                    Declined, expired, and cancelled offers from the last 90 days.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            @media (min-width: 769px) {
              .offer-history-page {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
              .offer-history-header-back-link {
                max-width: 1100px;
                width: 100%;
                margin-left: auto;
                margin-right: auto;
                padding-left: 2rem;
                padding-right: 2rem;
                box-sizing: border-box;
                display: flex !important;
                align-items: center;
              }
              .offer-history-header-expanded-inner {
                max-width: 1100px;
                width: 100%;
                margin-left: auto;
                margin-right: auto;
                padding-left: 2rem;
                padding-right: 2rem;
                box-sizing: border-box;
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                gap: 0.5rem 5rem;
                margin-bottom: 1.5rem;
              }
              .offer-history-header-copy {
                flex: 0 1 auto;
                min-width: 0;
              }
              .offer-history-header-expanded-inner h1 {
                text-align: left !important;
                margin: 0 !important;
              }
              .offer-history-header-description {
                margin: 0 !important;
                padding: 0 !important;
                text-align: left !important;
              }
              .tc-tabs,
              .tc-panel {
                max-width: 1100px !important;
                padding: 0 2rem !important;
                box-sizing: border-box !important;
              }
              .offer-history-login-prompt {
                max-width: 1100px !important;
                padding: 0 2rem !important;
                box-sizing: border-box !important;
              }
            }
            @media (max-width: 768px) {
              .offer-history-page {
                padding: 0 0.75rem !important;
                overflow-x: clip;
              }
              .offer-history-page-content {
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
              }
            }

            /* ---- Received / Sent tabs and offer bands ----
               Copied VERBATIM from the Offers page (TradeCenter.jsx) so the two
               pages look identical; if you change one, change the other (or
               extract both into a shared stylesheet). */
        .tc-tabs {
          max-width: 1100px;
          margin: 0 auto 1.25rem;
          width: 100%;
          box-sizing: border-box;
          display: flex;
          align-items: stretch;
          gap: 8px;
        }
        .tc-tab {
          /* Basis auto rather than 0: each pill starts at its label's natural
             width and the leftover space is split evenly on top, so "Sent"
             stays the narrow one instead of every pill being forced to the
             same width and leaving it looking hollow. */
          flex: 1 1 auto;
          justify-content: center;
          min-width: 0;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 0.55rem 1.1rem;
          border-radius: 9999px;
          border: 1px solid #e5e0d4;
          background-color: white;
          color: #6b7280;
          font-family: Nunito, sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          text-decoration: none;
          white-space: nowrap;
          cursor: pointer;
          transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .tc-tab:hover { border-color: #C89F3C; }
        .tc-tab.is-active {
          background-color: #C89F3C;
          border-color: #C89F3C;
          color: white;
        }
        .tc-tab-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 9999px;
          background-color: #ece4d2;
          color: #3f3f46;
          font-size: 0.78rem;
          font-weight: 800;
        }
        /* Translucent white on gold left the digit barely visible; a solid
           white chip with the gold read back out of it is legible in both
           states without changing the pill's shape. */
        .tc-tab.is-active .tc-tab-count {
          background-color: #ffffff;
          color: #9a7526;
        }

        .tc-panel { max-width: 1100px; margin: 0 auto; width: 100%; box-sizing: border-box; }
        .tc-list { display: flex; flex-direction: column; gap: 12px; }
        .tc-empty {
          font-size: 0.95rem; color: #9ca3af; text-align: center;
          padding: 3rem 1rem; margin: 0;
        }
        .tc-section-title { font-size: 1.05rem; font-weight: 700; color: #111827; margin: 0 0 4px; }
        .tc-section-count { color: #9ca3af; font-weight: 500; }
        .tc-section-sub { font-size: 0.85rem; color: #6b7280; margin: 0 0 12px; }

        .tc-band {
          background-color: white;
          border-radius: 14px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          overflow: hidden;
          text-align: left;
        }
        .tc-band-head {
          display: flex; align-items: center; gap: 10px;
          padding: 14px 18px;
          border-bottom: 1px solid #f3f0e8;
        }
        .tc-band-avatar {
          width: 38px; height: 38px; border-radius: 50%; overflow: hidden; flex-shrink: 0;
          background-color: #fef3c7; display: flex; align-items: center; justify-content: center;
          font-size: 0.75rem; font-weight: 700; color: #92400e;
        }
        .tc-band-who { flex: 1; min-width: 0; }
        .tc-band-name {
          font-weight: 700; font-size: 1rem; color: #111827;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .tc-band-from { font-weight: 500; color: #6b7280; margin-right: 6px; }
        .tc-band-date { font-size: 0.75rem; color: #9ca3af; }
        .tc-band-status { flex-shrink: 0; font-size: 0.95rem; font-weight: 700; white-space: nowrap; }

        .tc-band-body { display: flex; align-items: center; gap: 24px; padding: 16px 18px; }
        .tc-band-pedals {
          display: flex; align-items: center; gap: 22px; flex-shrink: 0;
          --tc-tile: 132px;
        }
        .tc-tile { width: var(--tc-tile); height: var(--tc-tile); flex-shrink: 0; }
        /* Overlap is a fraction of the tile so the fan-out looks the same at
           every size. */
        .tc-tile-stacked { margin-left: calc(var(--tc-tile) * -0.3); }
        .tc-band-side { text-align: center; }
        .tc-band-side { flex-shrink: 0; }
        .tc-band-side-label { font-size: 0.8rem; font-weight: 600; color: #6b7280; margin-top: 10px; }
        .tc-band-arrow { font-size: 30px; color: #9ca3af; flex-shrink: 0; }

        .tc-band-figures {
          display: flex; flex-direction: column; gap: 14px;
          align-self: stretch; align-items: flex-start; justify-content: center;
          padding-left: 28px; border-left: 1px solid #f0ece2;
        }
        .tc-band-figure { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        /* The label was a faint 0.68rem grey under a 1.3rem near-black value —
           too big a jump in both size and weight to read as one unit. Closing
           the size gap and darkening the label pairs them. */
        .tc-band-figure-label {
          white-space: nowrap;
          font-size: 0.72rem; font-weight: 700; color: #6b7280;
          letter-spacing: 0.06em; text-transform: uppercase;
        }
        .tc-band-figure-value { font-size: 1.05rem; font-weight: 700; color: #1f2937; white-space: nowrap; }
        .tc-band-amount {
          font-size: 1.05rem; display: flex; align-items: baseline; gap: 6px;
          flex-wrap: nowrap; white-space: nowrap;
        }
        .tc-band-amount-who { font-size: inherit; font-weight: inherit; color: inherit; }
        .tc-narrow-only { display: none; }

        .tc-band-actions { display: flex; gap: 10px; padding: 0 18px 14px; }
        .tc-band-cta {
          flex: 2; display: block; text-align: center; text-decoration: none;
          background-color: #5A9ECB; color: white; border-radius: 9999px;
          padding: 12px 20px; font-family: Nunito, sans-serif;
          font-weight: 700; font-size: 0.95rem;
          transition: opacity 0.15s ease;
        }
        .tc-band-cta:hover { opacity: 0.9; }
        .tc-band-secondary {
          flex: 1; background-color: #E5E7EB; color: #374151; border: none;
          border-radius: 9999px; padding: 12px 20px;
          font-family: Nunito, sans-serif; font-weight: 700; font-size: 0.95rem;
          cursor: pointer;
        }
        .tc-band-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

        .tc-band-foot {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
          padding: 14px 18px;
          background-color: #faf8f3;
          border-top: 1px solid #f3f0e8;
        }
        .tc-band-hint { font-size: 0.95rem; font-weight: 700; }
        .tc-band-foot-left {
          display: flex; flex-direction: column; align-items: flex-start;
          gap: 2px; min-width: 0;
        }
        .tc-band-foot-left .offer-seen-badge { font-size: 0.9rem; }
        .tc-band-expiry { font-size: 0.95rem; color: #6b7280; white-space: nowrap; }

        @media (max-width: 768px) {
          .tc-band-body { flex-direction: column; align-items: stretch; gap: 16px; }
          /* Four stacked tiles plus the other side plus the arrow is wider
             than a phone even after stepping the tile down, and shrinking far
             enough to fit that case would punish the common one- and two-pedal
             offers. So the row scrolls on its own instead — the page body must
             never scroll sideways. */
          .tc-band-pedals {
            /* "safe" keeps the pair centred normally but falls back to
               flex-start when it overflows, so a wide stack scrolls from its
               first tile instead of having that tile clipped off. */
            justify-content: center; justify-content: safe center;
            gap: 14px; --tc-tile: 108px;
            overflow-x: auto; -webkit-overflow-scrolling: touch;
            scrollbar-width: none; padding-bottom: 2px;
          }
          .tc-band-pedals::-webkit-scrollbar { display: none; }
          .tc-band-arrow { font-size: 24px; }
          .tc-band-figures {
            flex-direction: row; justify-content: space-between; gap: 12px;
            align-self: auto; padding-left: 0; border-left: none;
          }
          .tc-band-figure { gap: 2px; }
          .tc-band-figure-label { white-space: normal; }
          .tc-band-figure-value { white-space: normal; }
          .tc-band-amount { font-size: 1.15rem; flex-wrap: wrap; white-space: normal; }
          .tc-band-amount-who { font-size: 0.85rem; font-weight: 600; }
          .tc-band-actions { flex-direction: column; }
          .tc-band-secondary { width: 100%; }
          .tc-wide-only { display: none; }
          .tc-narrow-only { display: inline; }
          .tc-band-foot { flex-wrap: wrap; row-gap: 4px; padding: 10px 18px; }
          .tc-band-hint { font-size: 0.8rem; }
          .tc-band-foot-left .offer-seen-badge { font-size: 0.8rem; }
          .tc-band-expiry { font-size: 0.8rem; margin-left: auto; }
        }

            /* History-only additions. */
            .oh-band-resend {
              background-color: white; color: #5A9ECB;
              border: 2px solid #5A9ECB;
            }
            .oh-band-resend-msg {
              font-size: 0.8rem; color: #92400e; margin: -6px 18px 12px;
              text-align: center; line-height: 1.4;
            }
            .oh-band-reason { font-size: 0.9rem; font-style: italic; color: #6b7280; }
          `}</style>

          {!currentUser ? (
            <div className="offer-history-login-prompt" style={{ margin: '2rem auto', maxWidth: '600px' }}>
              <div className='mb-4 p-4 border border-blue-300 rounded-lg bg-blue-50 text-center'>
                <p className='text-blue-800 text-lg font-semibold mb-2'>Log in to view your offer history.</p>
                <Link to='/login' className='inline-block px-4 py-2 text-sm font-medium' style={{ color: '#5A9ECB', textDecoration: 'none' }}>
                  Log In
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="tc-tabs" role="tablist" aria-label="Offer history lists">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'received'}
                  className={`tc-tab ${activeTab === 'received' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('received')}
                >
                  Received {listsReady && <span className="tc-tab-count">{receivedOffers.length}</span>}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'sent'}
                  className={`tc-tab ${activeTab === 'sent' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('sent')}
                >
                  Sent {listsReady && <span className="tc-tab-count">{sentOffers.length}</span>}
                </button>
              </div>

              <div className="tc-panel">
                {/* All four lists, not just the first: `loading` clears on the
                    sent-trades listener, so the default Received tab could
                    briefly claim "No past offers received". */}
                {(loading || !listsReady) ? (
                  <p className="tc-empty">Loading offer history...</p>
                ) : activeTab === 'received' ? (
                  receivedOffers.length === 0 ? (
                    <p className="tc-empty">No past offers received in the last 90 days.</p>
                  ) : (
                    <div className="tc-list">
                      {receivedOffers.map((offer) => renderOfferBand(offer, 'received'))}
                    </div>
                  )
                ) : (
                  sentOffers.length === 0 ? (
                    <p className="tc-empty">No past offers sent in the last 90 days.</p>
                  ) : (
                    <div className="tc-list">
                      {sentOffers.map((offer) => renderOfferBand(offer, 'sent'))}
                    </div>
                  )
                )}
              </div>
            </>
          )}

          <div style={{ height: '200px' }}></div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OfferHistory;
